import { selectDimensions } from "../../../../store/selectors/metaDataSelectors.ts";
import useRequestConfigBuilder from "../../tabular/hooks/useRequestConfigBuilder.ts";
import { useBiApiClientProvider } from "../../../../contexts/BiApiClientProviderContext.ts";
import type { BiApiClient } from "../../../../contexts/ApiClientProviderContext.ts";
import { getTabularExportWarning } from "../../../common/export/exportWarnings.ts";
import ExcelReportExport from "../../../common/export/ExcelReportExport.tsx";
import { Box, Grid, Stack } from "@mui/material";
import { useSelector } from "react-redux";
import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";
import { selectSessionId } from "../../../../store/selectors/currentReportSelectors";
import { selectLogQueries } from "../../../../store/selectors/devToolsSelectors";
import { selectSorts, selectTabularFields } from "../../../../store/selectors/fieldsStateSelectors";
import PartContainer from "../../../viewer/common/PartContainer.tsx";
import PreviewActions from "../../../viewer/common/PreviewActions.tsx";
import EditingHistoryControls from "../../common/EditingHistoryControls.tsx";
import BuildProgressWrapper from "../../BuildProgressWrapper.tsx";
import ChangePreviewModeButton from "../../common/ChangePreviewModeButton.tsx";
import BuilderMoreAction from "../../common/fields/BuilderMoreAction.tsx";
import useGridStateBuilder from "../../tabular/hooks/useGridStateBuilder.ts";
import useTrackMeasuresListUpdate from "../../tabular/hooks/useTrackMeasuresListUpdate.ts";
import EditPreviewComponent from "../../tabular/preview/EditPreviewComponent.tsx";
import PreviewTabs from "../PreviewTabs.tsx";
import RequestCode from "./code/RequestCode.tsx";
import ResponseCode from "./code/ResponseCode.tsx";
import TableComponent from "./TableComponent.tsx";

const ApiTabularEditor = () => {
  const state = useGridStateBuilder();
  const { requestConfig } = useRequestConfigBuilder();
  const dimensions = useSelector(selectDimensions);
  const { requestBuildTabularExport } = useBiApiClientProvider<BiApiClient>();
  const fields = useSelector(selectTabularFields);
  const sorts = useSelector(selectSorts);
  const isQueryLoggingActive = useSelector(selectLogQueries);
  const sessionId = useSelector(selectSessionId);
  const { onRefreshGrid } = useBiCommonDataContext();

  useTrackMeasuresListUpdate();

  return (
    <Stack direction="row" sx={{ flex: 1 }}>
      <PartContainer
        sx={{ maxWidth: "24rem", minWidth: "24rem" }}
        caption={<Box sx={{ pl: "1.3rem" }}>Builder</Box>}
        actions={<BuilderMoreAction />}
      >
        <TableComponent />
      </PartContainer>
      <BuildProgressWrapper>
        <PreviewTabs
          GridView={
            <PartContainer
              sx={{ borderRight: "unset", p: "1.3rem", pt: 2, height: "100%" }}
              containerSx={{ position: "relative", gap: "1rem" }}
              caption="Preview"
              actions={
                <PreviewActions
                  onRefresh={onRefreshGrid}
                  ChangePreviewModeButton={<ChangePreviewModeButton />}
                  EditingHistory={<EditingHistoryControls />}
                />
              }
            >
              <ExcelReportExport
                mode="build"
                requestConfig={requestConfig}
                dimensions={dimensions}
                requestExport={requestBuildTabularExport}
                warning={getTabularExportWarning(state)}
              />
              <EditPreviewComponent gridState={state} />
            </PartContainer>
          }
          CodeView={
            <Grid
              container
              sx={{
                display: "grid",
                width: "100%",
                flexWrap: "nowrap",
                minWidth: "45rem",
                gap: 3,
                p: "1.3rem",
                overflow: "hidden",
                gridTemplateRows: "minmax(20%, auto) minmax(20%, auto)",
              }}
            >
              <RequestCode
                isLoading={state.loading}
                fields={fields}
                sorting={sorts}
                isQueryLoggingActive={!!isQueryLoggingActive}
                sessionId={sessionId}
              />
              <ResponseCode isLoading={state.loading} />
            </Grid>
          }
        />
      </BuildProgressWrapper>
    </Stack>
  );
};

export default ApiTabularEditor;
